/**
 * The Pick'em Game tab's staged picks (ISSUE-1304, ISSUE-1309): what a fan has
 * tapped since the last save, and the one all-or-nothing `/pickem/picks-submit`
 * body that saves it.
 *
 * Server resolves, client renders. Which games are pickable, each game's lock,
 * how many picks the round needs, the highest confidence rank and the ranks
 * already locked all come from `/pickem/round-get`. This module only edits a
 * draft against those facts: it never decides a game is open, never computes
 * a rank domain of its own and never scores anything.
 *
 * Confidence is built for one thumb. Tapping a team gives the pick the lowest
 * free rank, and the order list moves a pick up or down one step at a time,
 * trading places with whatever pick holds the rank it lands on.
 *
 * No React, no clock reads, no SDK calls.
 */
import type {
  typePickemGame,
  typePickemRoundData,
  typePickemSubmitBody,
} from "./pickemTypes";

export type typeStagedPick = {
  eventId: string;
  teamId: string;
  confidenceRank: number | null;
  acceptsPendingSpread: boolean;
  /** Saved and past kickoff. A locked pick is shown, never edited. */
  isLocked: boolean;
};

export type typePickemStage = {
  roundId: string;
  picks: Readonly<Record<string, typeStagedPick>>;
  /** The tiebreaker box exactly as typed. */
  tiebreakerText: string;
};

export type typeSelectTeamResult =
  { ok: true; stage: typePickemStage } | { ok: false; reason: "full" };

export function stageFromRound(round: typePickemRoundData): typePickemStage {
  const picks: Record<string, typeStagedPick> = {};
  for (const game of round.games) {
    const myPick = game.myPick;
    if (myPick === null || myPick === undefined) continue;
    picks[game.eventId] = {
      eventId: game.eventId,
      teamId: myPick.teamId,
      confidenceRank: myPick.confidenceRank ?? null,
      acceptsPendingSpread: myPick.acceptsPendingSpread,
      isLocked: myPick.isLocked || game.isLocked,
    };
  }
  const guess = round.tiebreaker?.myGuess;
  return {
    roundId: round.round.roundId,
    picks,
    tiebreakerText: guess === null || guess === undefined ? "" : String(guess),
  };
}

/** Ranks a staged pick may take: 1 to maxRank, minus the locked ones. */
export function assignableRanks(round: typePickemRoundData): number[] {
  const confidence = round.confidence;
  if (confidence === null) return [];
  const locked = new Set(confidence.lockedRanks);
  const ranks: number[] = [];
  for (let rank = 1; rank <= confidence.maxRank; rank += 1) {
    if (!locked.has(rank)) ranks.push(rank);
  }
  return ranks;
}

function rankHolder(
  stage: typePickemStage,
  rank: number,
  exceptEventId: string,
): typeStagedPick | null {
  for (const pick of Object.values(stage.picks)) {
    if (pick.eventId !== exceptEventId && pick.confidenceRank === rank) {
      return pick;
    }
  }
  return null;
}

function lowestFreeRank(
  round: typePickemRoundData,
  stage: typePickemStage,
): number | null {
  for (const rank of assignableRanks(round)) {
    if (rankHolder(stage, rank, "") === null) return rank;
  }
  return null;
}

export function stagedPickCount(stage: typePickemStage): number {
  return Object.keys(stage.picks).length;
}

/**
 * Tapping a team. The same team again clears the pick; the other team swaps
 * it and keeps its rank; a new pick takes the lowest free rank. A new pick
 * past the round's required count is refused, so the fan clears one first.
 */
export function selectTeam(input: {
  round: typePickemRoundData;
  stage: typePickemStage;
  eventId: string;
  teamId: string;
  acceptsPendingSpread: boolean;
}): typeSelectTeamResult {
  const { round, stage } = input;
  const existing = stage.picks[input.eventId];
  if (existing?.isLocked === true) return { ok: true, stage };
  if (existing !== undefined && existing.teamId === input.teamId) {
    return { ok: true, stage: removePick({ stage, eventId: input.eventId }) };
  }
  if (existing !== undefined) {
    return {
      ok: true,
      stage: {
        ...stage,
        picks: {
          ...stage.picks,
          [input.eventId]: {
            ...existing,
            teamId: input.teamId,
            acceptsPendingSpread: input.acceptsPendingSpread,
          },
        },
      },
    };
  }
  if (stagedPickCount(stage) >= round.picksRequired) {
    return { ok: false, reason: "full" };
  }
  return {
    ok: true,
    stage: {
      ...stage,
      picks: {
        ...stage.picks,
        [input.eventId]: {
          eventId: input.eventId,
          teamId: input.teamId,
          confidenceRank:
            round.confidence === null ? null : lowestFreeRank(round, stage),
          acceptsPendingSpread: input.acceptsPendingSpread,
          isLocked: false,
        },
      },
    },
  };
}

export function removePick(input: {
  stage: typePickemStage;
  eventId: string;
}): typePickemStage {
  const existing = input.stage.picks[input.eventId];
  if (existing === undefined || existing.isLocked) return input.stage;
  const picks = Object.fromEntries(
    Object.entries(input.stage.picks).filter(
      ([eventId]) => eventId !== input.eventId,
    ),
  );
  return { ...input.stage, picks };
}

/**
 * Puts a pick on a rank. Whatever unlocked pick held that rank takes this
 * pick's old one, so two picks never share a rank and a locked rank never
 * moves.
 */
export function setPickRank(input: {
  round: typePickemRoundData;
  stage: typePickemStage;
  eventId: string;
  rank: number;
}): typePickemStage {
  const { stage } = input;
  const pick = stage.picks[input.eventId];
  if (pick === undefined || pick.isLocked) return stage;
  if (!assignableRanks(input.round).includes(input.rank)) return stage;
  if (pick.confidenceRank === input.rank) return stage;
  const holder = rankHolder(stage, input.rank, input.eventId);
  if (holder?.isLocked === true) return stage;
  const picks: Record<string, typeStagedPick> = {
    ...stage.picks,
    [pick.eventId]: { ...pick, confidenceRank: input.rank },
  };
  if (holder !== null) {
    picks[holder.eventId] = { ...holder, confidenceRank: pick.confidenceRank };
  }
  return { ...stage, picks };
}

/** "up" is more sure, which is the next higher rank a pick can take. */
export function movePickRank(input: {
  round: typePickemRoundData;
  stage: typePickemStage;
  eventId: string;
  direction: "up" | "down";
}): typePickemStage {
  const pick = input.stage.picks[input.eventId];
  const current = pick?.confidenceRank ?? null;
  if (pick === undefined || current === null) return input.stage;
  const ranks = assignableRanks(input.round);
  const target =
    input.direction === "up"
      ? ranks.find((rank) => rank > current)
      : [...ranks].reverse().find((rank) => rank < current);
  if (target === undefined) return input.stage;
  return setPickRank({ ...input, rank: target });
}

export type typeConfidenceRow = {
  eventId: string;
  teamId: string;
  rank: number;
  isLocked: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
};

/** Ranked picks, most sure first, with which way each one can still move. */
export function buildConfidenceRows(input: {
  round: typePickemRoundData;
  stage: typePickemStage;
}): typeConfidenceRow[] {
  const ranks = assignableRanks(input.round);
  const highest = ranks.at(-1) ?? 0;
  const lowest = ranks[0] ?? 0;
  return Object.values(input.stage.picks)
    .filter(
      (pick): pick is typeStagedPick & { confidenceRank: number } =>
        pick.confidenceRank !== null,
    )
    .sort((left, right) => right.confidenceRank - left.confidenceRank)
    .map((pick) => ({
      eventId: pick.eventId,
      teamId: pick.teamId,
      rank: pick.confidenceRank,
      isLocked: pick.isLocked,
      canMoveUp: !pick.isLocked && pick.confidenceRank < highest,
      canMoveDown: !pick.isLocked && pick.confidenceRank > lowest,
    }));
}

export type typeTiebreakerParse =
  { ok: true; guess: number | null } | { ok: false };

/** Empty clears the guess; anything else must be a whole number 0 to 999. */
export function parseTiebreakerText(text: string): typeTiebreakerParse {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, guess: null };
  if (!/^\d{1,3}$/.test(trimmed)) return { ok: false };
  return { ok: true, guess: Number(trimmed) };
}

function savedPickFor(game: typePickemGame): typeStagedPick | null {
  const myPick = game.myPick;
  if (myPick === null || myPick === undefined) return null;
  return {
    eventId: game.eventId,
    teamId: myPick.teamId,
    confidenceRank: myPick.confidenceRank ?? null,
    acceptsPendingSpread: myPick.acceptsPendingSpread,
    isLocked: myPick.isLocked || game.isLocked,
  };
}

/**
 * The submit body, or null when nothing changed. Only picks that differ from
 * what is saved go up, because every saved edit takes the spread line again;
 * an untouched pick keeps the line it was saved with.
 */
export function buildSubmitBody(input: {
  poolId: string;
  round: typePickemRoundData;
  stage: typePickemStage;
}): typePickemSubmitBody | null {
  const { round, stage } = input;
  const isConfidence = round.confidence !== null;
  const picks: typePickemSubmitBody["picks"] = [];
  const removeEventIds: string[] = [];

  for (const game of round.games) {
    const saved = savedPickFor(game);
    const staged = stage.picks[game.eventId];
    if (saved?.isLocked === true) continue;
    if (staged === undefined) {
      if (saved !== null) removeEventIds.push(game.eventId);
      continue;
    }
    if (
      saved !== null &&
      saved.teamId === staged.teamId &&
      saved.confidenceRank === staged.confidenceRank
    ) {
      continue;
    }
    picks.push({
      eventId: game.eventId,
      teamId: staged.teamId,
      ...(isConfidence ? { confidenceRank: staged.confidenceRank } : {}),
      acceptsPendingSpread: staged.acceptsPendingSpread,
    });
  }

  const body: typePickemSubmitBody = {
    poolId: input.poolId,
    roundId: round.round.roundId,
    picks,
    removeEventIds,
  };

  const tiebreaker = round.tiebreaker;
  if (tiebreaker !== null && !tiebreaker.isLocked) {
    const parsed = parseTiebreakerText(stage.tiebreakerText);
    const savedGuess = tiebreaker.myGuess ?? null;
    if (parsed.ok && parsed.guess !== savedGuess) {
      body.tiebreakerGuess = parsed.guess;
    }
  }

  const hasChange =
    picks.length > 0 ||
    removeEventIds.length > 0 ||
    body.tiebreakerGuess !== undefined;
  return hasChange ? body : null;
}

export function isStageDirty(input: {
  round: typePickemRoundData;
  stage: typePickemStage;
}): boolean {
  if (input.stage.roundId !== input.round.round.roundId) return false;
  if (buildSubmitBody({ poolId: "", ...input }) !== null) return true;
  // A tiebreaker box holding something that is not a number is unsaved too.
  return (
    input.round.tiebreaker !== null &&
    !input.round.tiebreaker.isLocked &&
    !parseTiebreakerText(input.stage.tiebreakerText).ok
  );
}
